import React, { useState, useRef } from 'react';
import { Button, Textarea, Text, Stack, Collapse } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faWandMagicSparkles, 
  faPlay, 
  faPause,
  faVolumeHigh,
  faDownload,
  faRefresh,
  faCheck,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import { useNotification } from '../../context/NotificationContext';
import { getApiUrl } from '../../config/api';
import styles from './AudioModal.module.css'; // Reuse existing styles
import CustomSlider from './CustomSlider';

interface SoundEffectsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSoundEffectGenerated: (soundEffectUrl: string, filename: string, duration: number) => void;
  userId: string;
}

interface GenerateEnvironmentalSoundRequest {
  prompt: string;
  duration?: number;
  temperature?: number;
  top_k?: number;
  top_p?: number;
  num_samples?: number;
  userId: string;
}

interface GenerateEnvironmentalSoundResponse {
  success: boolean;
  message: string;
  data: {
    userId: string;
    prompt: string;
    soundEffects: Array<{
      index: number;
      s3Url: string;
      duration: number;
      filename: string;
    }>;
    count: number;
  };
}

// Popular sound effect prompts for quick selection
const POPULAR_PROMPTS = [
  { prompt: "gentle rain on forest leaves", icon: "🌧️", category: "Nature" },
  { prompt: "ocean waves crashing on beach", icon: "🌊", category: "Nature" },
  { prompt: "crackling campfire at night", icon: "🔥", category: "Nature" },
  { prompt: "busy city street with traffic", icon: "🏙️", category: "Urban" },
  { prompt: "thunderstorm with lightning", icon: "⛈️", category: "Nature" },
  { prompt: "birds chirping in morning", icon: "🐦", category: "Nature" },
  { prompt: "coffee shop ambiance", icon: "☕", category: "Indoor" },
  { prompt: "typing on mechanical keyboard", icon: "⌨️", category: "Office" },
  { prompt: "footsteps on gravel path", icon: "👣", category: "Movement" },
  { prompt: "clock ticking in quiet room", icon: "🕐", category: "Indoor" },
  { prompt: "wind blowing through trees", icon: "🌳", category: "Nature" },
  { prompt: "distant church bells ringing", icon: "🔔", category: "Urban" }
];

// Temperature options for sound generation
const TEMPERATURE_OPTIONS = [
  { value: 0.5, label: 'Focused', description: 'More predictable and coherent sounds' },
  { value: 1.0, label: 'Balanced', description: 'Good balance of creativity and coherence' },
  { value: 1.5, label: 'Creative', description: 'More variation and unexpected elements' },
  { value: 2.0, label: 'Experimental', description: 'Maximum creativity and variation' },
];

// Custom Select Component
interface CustomSelectProps {
  data: Array<{ value: string; label: string; description?: string }>;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({ data, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const selectRef = useRef<HTMLDivElement>(null);
  
  const selectedItem = data.find(item => item.value === value);
  const filteredData = data.filter(item => 
    item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={styles.customSelect} ref={selectRef}>
      <div 
        className={styles.selectTrigger} 
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={styles.selectValue}>
          {selectedItem ? selectedItem.label : placeholder || 'Select an option'}
        </span>
        <FontAwesomeIcon 
          icon={faChevronDown} 
          className={`${styles.selectArrow} ${isOpen ? styles.selectArrowOpen : ''}`}
        />
      </div>
      
      {isOpen && (
        <div className={styles.selectDropdown}>
          {data.length > 5 && (
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.selectSearch}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <div className={styles.selectOptions}>
            {filteredData.map((item) => (
              <div
                key={item.value}
                className={`${styles.selectOption} ${item.value === value ? styles.selectOptionSelected : ''}`}
                onClick={() => handleSelect(item.value)}
              >
                <div className={styles.selectOptionLabel}>{item.label}</div>
                {item.description && (
                  <div className={styles.selectOptionDescription}>{item.description}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SoundEffectsModal: React.FC<SoundEffectsModalProps> = ({ 
  isOpen, 
  onClose, 
  onSoundEffectGenerated,
  userId
}) => {
  const [activeStep, setActiveStep] = useState<'generation' | 'result'>('generation');
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Form state
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState(10.0);
  const [temperature, setTemperature] = useState(1.0);
  const [topK, setTopK] = useState(250);
  const [topP, setTopP] = useState(0.0);
  const [numSamples, setNumSamples] = useState(1);
  
  // Result state
  const [generatedSoundEffects, setGeneratedSoundEffects] = useState<Array<{
    index: number;
    s3Url: string;
    duration: number;
    filename: string;
  }>>([]);
  const [selectedSoundEffectIndex, setSelectedSoundEffectIndex] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  
  // Accordion state
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  
  const { showNotification } = useNotification();

  // Reset form when modal closes
  const handleClose = () => {
    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
    }
    setIsPlaying(false);
    setActiveStep('generation');
    setPrompt('');
    setGeneratedSoundEffects([]);
    onClose();
  };

  // Generate environmental sound
  const generateEnvironmentalSound = async (): Promise<GenerateEnvironmentalSoundResponse> => {
    const request: GenerateEnvironmentalSoundRequest = {
      prompt: prompt,
      duration: duration,
      temperature: temperature,
      top_k: topK,
      top_p: topP,
      num_samples: numSamples,
      // 🔒 NOTE: userId included for backward compatibility but backend uses authenticated user ID
      userId: userId,
    };

    const response = await fetch(getApiUrl('/music-generation/environmental-sound'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  };

  // Handle generation
  const handleGenerate = async () => {
    if (!prompt.trim()) {
      showNotification({
        title: 'Missing Description',
        message: 'Please enter a description for the sound effect',
        type: 'error'
      });
      return;
    }

    if (!userId) {
      showNotification({
        title: 'Authentication Required',
        message: 'Please log in to generate sound effects',
        type: 'error'
      });
      return;
    }

    setIsGenerating(true);

    try {
      const response = await generateEnvironmentalSound();
      if (response.success) {
        // Handle the response structure from the backend
        const soundEffects = response.data.soundEffects || [];
        setGeneratedSoundEffects(soundEffects);
        setActiveStep('result');
        // showNotification({
        //   title: 'Success',
        //   message: `Generated ${soundEffects.length} sound effect(s) successfully!`,
        //   type: 'success'
        // });
      }
    } catch (error) {
      console.error('Generation failed:', error);
      showNotification({
        title: 'Generation Failed',
        message: 'Failed to generate sound effect. Please try again.',
        type: 'error'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle audio playback
  const handlePlayPause = (audioUrl: string) => {
    if (currentAudio && !currentAudio.paused) {
      currentAudio.pause();
      setIsPlaying(false);
      setCurrentAudio(null);
    } else {
      if (currentAudio) {
        currentAudio.pause();
      }
      
      const audio = new Audio(audioUrl);
      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        setCurrentAudio(null);
      });
      
      audio.play();
      setIsPlaying(true);
      setCurrentAudio(audio);
    }
  };

  // Handle regeneration
  const handleRegenerate = () => {
    setActiveStep('generation');
    setGeneratedSoundEffects([]);
    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
    }
    setIsPlaying(false);
  };

  // Handle completion - adds to timeline and refreshes sound effects list
  const handleComplete = () => {
    const selectedSoundEffect = generatedSoundEffects[selectedSoundEffectIndex];
    if (selectedSoundEffect) {
      onSoundEffectGenerated(
        selectedSoundEffect.s3Url, 
        selectedSoundEffect.filename, 
        selectedSoundEffect.duration
      );
      handleClose();
    }
  };

  // Handle popular prompt selection
  const handlePopularPromptSelect = (selectedPrompt: string) => {
    setPrompt(selectedPrompt);
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {activeStep === 'generation' ? 'Generate Sound Effect' : 'Generated Sound Effect'}
          </h2>
          <button className={styles.closeButton} onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {activeStep === 'generation' ? (
          <div className={styles.generationContent}>
            <Stack gap="xl">
              <div>
                <Text size="sm" fw={500} mb="xs">Sound Description *</Text>
                <Textarea
                  placeholder="Describe the sound effect you want to generate (e.g., 'gentle rain', 'busy city street', 'crackling campfire')"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  minRows={3}
                  maxRows={6}
                />
              </div>

              {/* Popular Prompts */}
              <div>
                <Text size="sm" fw={500} mb="xs">Popular Sound Effects</Text>
                <div className={styles.voiceTemplates}>
                  {POPULAR_PROMPTS.map((item, index) => (
                    <div
                      key={index}
                      className={`${styles.voiceTemplate} ${
                        prompt === item.prompt ? styles.voiceTemplateSelected : ''
                      }`}
                      onClick={() => handlePopularPromptSelect(item.prompt)}
                    >
                      <div className={styles.voiceTemplateContent}>
                        <div className={styles.voiceTemplateLabel}>
                          {item.icon} {item.prompt}
                        </div>
                        <div className={styles.voiceTemplateTone}>
                          {item.category}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <CustomSlider
                value={duration}
                onChange={setDuration}
                min={1.0}
                max={30.0}
                step={0.5}
                marks={[
                  { value: 5, label: '5s' },
                  { value: 10, label: '10s' },
                  { value: 15, label: '15s' },
                  { value: 30, label: '30s' }
                ]}
                label="Duration (seconds)"
                description={`Current: ${duration} seconds`}
              />

              {/* Advanced Configuration Accordion */}
              <div className={styles.accordionContainer}>
                <button
                  className={styles.accordionTrigger}
                  onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                  type="button"
                >
                  <span className={styles.accordionTitle}>Advanced Configuration</span>
                  <FontAwesomeIcon 
                    icon={isAdvancedOpen ? faChevronUp : faChevronDown} 
                    className={styles.accordionArrow}
                  />
                </button>
                
                <Collapse in={isAdvancedOpen}>
                  <div className={styles.accordionContent}>
                    <Stack gap="md" pt="md">
                      <div>
                        <Text size="sm" fw={500} mb="xs">Creativity Level</Text>
                        <CustomSelect
                          data={TEMPERATURE_OPTIONS.map(temp => ({
                            value: temp.value.toString(),
                            label: temp.label,
                            description: temp.description
                          }))}
                          value={temperature.toString()}
                          onChange={(value) => setTemperature(parseFloat(value))}
                          placeholder="Select creativity level"
                        />
                        <Text size="xs" color="dimmed" mt="xs">
                          {TEMPERATURE_OPTIONS.find(t => t.value === temperature)?.description}
                        </Text>
                      </div>

                      <CustomSlider
                        value={topK}
                        onChange={setTopK}
                        min={1}
                        max={1000}
                        step={10}
                        marks={[
                          { value: 50, label: 'Focused' },
                          { value: 250, label: 'Balanced' },
                          { value: 500, label: 'Diverse' },
                          { value: 1000, label: 'Very Diverse' }
                        ]}
                        label="Sound Diversity (Top-K)"
                        description={`Current: ${topK} (Higher = more diverse sounds)`}
                      />

                      <CustomSlider
                        value={numSamples}
                        onChange={setNumSamples}
                        min={1}
                        max={3}
                        step={1}
                        marks={[
                          { value: 1, label: '1 Sample' },
                          { value: 2, label: '2 Samples' },
                          { value: 3, label: '3 Samples' }
                        ]}
                        label="Number of Variations"
                        description={`Generate ${numSamples} variation(s) to choose from`}
                      />
                    </Stack>
                  </div>
                </Collapse>
              </div>
            </Stack>

            <div className={styles.modalActions}>
              <button onClick={handleClose} className={styles.cancelButton}>
                Cancel
              </button>
              <Button 
                onClick={handleGenerate} 
                loading={isGenerating}
                className={styles.primaryButton}
              >
                <FontAwesomeIcon icon={faWandMagicSparkles} style={{marginRight: "8px"}} />
                {isGenerating ? 'Generating...' : 'Generate Sound Effect'}
              </Button>
            </div>
          </div>
        ) : (
          // Result step
          <div className={styles.resultSection}>
            {generatedSoundEffects.length > 0 ? (
              <div className={styles.melodyResults}>
                <h3>Choose Your Sound Effect</h3>
                <p>We generated {generatedSoundEffects.length} variation(s) for you:</p>
                
                <div className={styles.melodyOptions}>
                  {generatedSoundEffects.map((soundEffect, index) => (
                    <div 
                      key={soundEffect.index}
                      className={`${styles.melodyOption} ${selectedSoundEffectIndex === index ? styles.selected : ''}`}
                      onClick={() => setSelectedSoundEffectIndex(index)}
                    >
                      <div className={styles.melodyHeader}>
                        <h4>Sound Effect {soundEffect.index}</h4>
                        <span className={styles.duration}>{soundEffect.duration.toFixed(1)}s</span>
                      </div>
                      <div className={styles.melodyControls}>
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlayPause(soundEffect.s3Url);
                          }}
                          leftSection={<FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />}
                        >
                          {isPlaying ? 'Pause' : 'Play'}
                        </Button>
                        <Button
                          size="sm"
                          component="a"
                          href={soundEffect.s3Url}
                          download={soundEffect.filename}
                          variant="outline"
                          leftSection={<FontAwesomeIcon icon={faDownload} />}
                        >
                          Download
                        </Button>
                      </div>
                      {selectedSoundEffectIndex === index && (
                        <div className={styles.selectedIndicator}>
                          <FontAwesomeIcon icon={faCheck} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className={styles.modalActions}>
              <button 
                onClick={handleRegenerate}
                className={styles.cancelButton}
              >
                <FontAwesomeIcon icon={faRefresh} style={{marginRight: "8px"}} />
                Regenerate
              </button>
              <Button 
                onClick={handleComplete}
                className={styles.primaryButton}
              >
                <FontAwesomeIcon icon={faCheck} style={{marginRight: "8px"}} />
                Add to Timeline
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SoundEffectsModal;